import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountCard, AccountSection } from './AccountUI';

const roboman_api = process.env.REACT_APP_ROBOMAN_API;
const DEFAULT_LINKEDIN_FORM = {
    connection_method: 'credentials',
    username: '',
    password: '',
    checkpoint_code: '',
};
const DEFAULT_SMTP_FORM = {
    imap_user: '',
    smtp_user: '',
    imap_password: '',
    smtp_password: '',
    imap_host: '',
    imap_port: '',
    smtp_host: '',
    smtp_port: '',
    imap_encryption: 'SSL',
};

// --- ICONS (for logos and UI elements) ---

const SearchIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
);
const LinkedInLogo = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none">
        <rect x="2" y="2" width="20" height="20" rx="4" fill="#0A66C2" />
        <path d="M8.2 9.3a1.15 1.15 0 1 0 0-2.3 1.15 1.15 0 0 0 0 2.3Zm-1 1.5h2v6.2h-2v-6.2Zm3.3 0h1.9v.85h.03c.27-.5.92-1.02 1.9-1.02 2.03 0 2.4 1.33 2.4 3.05v3.29h-2v-2.92c0-.7-.01-1.6-.98-1.6-.98 0-1.13.76-1.13 1.55v2.97h-2v-6.2Z" fill="white" />
    </svg>
);
const DomainLogo = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18" />
        <path d="M12 3c2.8 2.4 4.5 5.6 4.5 9S14.8 18.6 12 21c-2.8-2.4-4.5-5.6-4.5-9S9.2 5.4 12 3Z" />
    </svg>
);
const EmailLogo = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m4 7 8 6 8-6" />
    </svg>
);
const SmtpLogo = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H17a3 3 0 0 1 0 6H7" />
        <path d="M7 11H5a2 2 0 1 0 0 4h11.5a2.5 2.5 0 1 1 0 5H7" />
    </svg>
);
const CloseIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
);
const TrashIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="m19 6-1 14H6L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></svg>
);
const EyeIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" /><circle cx="12" cy="12" r="3" /></svg>
);
const EyeOffIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m15 18-.722-3.25" /><path d="M2 8a10.645 10.645 0 0 0 20 0" /><path d="m20 15-1.726-2.05" /><path d="m4 15 1.726-2.05" /><path d="m9 18 .722-3.25" /></svg>
);

const BASE_STATIC_INTEGRATIONS = [
    {
        id: 'domain-email',
        name: 'Domain / Email Integrations',
        description: 'Verify your sending domain and connect your mailbox for branded outreach.',
        icon: DomainLogo,
        accentClass: 'bg-amber-50 text-amber-600',
        isConnected: false,
        actionLabel: 'Manage',
        isActionDisabled: false,
    },
];

// --- REUSABLE UI COMPONENTS ---
const SectionCard = ({ children, title, subtitle }) => (
    <AccountSection eyebrow="Channels" title={title} subtitle={subtitle}>
        {children}
    </AccountSection>
);
const Card = ({ children, className }) => (
    <AccountCard className={`overflow-hidden border border-slate-200/80 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.08),_transparent_28%),linear-gradient(180deg,_rgba(255,255,255,0.98),_rgba(248,250,252,0.98))] p-4 sm:p-5 ${className}`}>
        {children}
    </AccountCard>
);
const LoadingCards = () => (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {[1, 2, 3].map(i => (
            <div key={i} className="min-h-[220px] w-full animate-pulse rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
                <div className="flex items-start justify-between gap-3">
                    <div className="h-11 w-11 rounded-2xl bg-slate-200"></div>
                    <div className="h-6 w-24 rounded-full bg-slate-200"></div>
                </div>
                <div className="mt-5 h-4 w-2/3 rounded-full bg-slate-200"></div>
                <div className="mt-3 h-3 w-full rounded-full bg-slate-200"></div>
                <div className="mt-2 h-3 w-4/5 rounded-full bg-slate-200"></div>
                <div className="mt-3 h-3 w-1/2 rounded-full bg-slate-200"></div>
                <div className="mt-8 border-t border-slate-100 pt-4">
                    <div className="h-9 w-28 rounded-xl bg-slate-200"></div>
                </div>
            </div>
        ))}
    </div>
);

const SummaryChip = ({ label, value, tone = 'slate' }) => {
    const toneClasses = {
        slate: 'border-slate-200 bg-white text-slate-700',
        blue: 'border-blue-200 bg-blue-50/80 text-blue-700',
        green: 'border-emerald-200 bg-emerald-50/80 text-emerald-700',
        amber: 'border-amber-200 bg-amber-50/80 text-amber-700',
    };

    return (
        <div className={`min-w-[120px] rounded-2xl border px-3 py-3 ${toneClasses[tone] || toneClasses.slate}`}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-75">{label}</p>
            <p className="mt-1 text-lg font-semibold tracking-tight">{value}</p>
        </div>
    );
};

const FormField = ({ label, required = false, children }) => (
    <label className="block">
        <span className="mb-2 block text-sm font-medium text-slate-700">
            {label}
            {required && <span className="text-red-500"> *</span>}
        </span>
        {children}
    </label>
);

const ConfirmModal = ({
    isOpen,
    title,
    message,
    confirmLabel,
    onCancel,
    onConfirm,
    isConfirming,
    tone = 'danger',
}) => {
    if (!isOpen) {
        return null;
    }

    const confirmButtonClassName = tone === 'danger'
        ? 'bg-red-600 text-white hover:bg-red-700'
        : 'bg-blue-600 text-white hover:bg-blue-700';

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.35)]">
                <div className="border-b border-slate-200 px-6 py-5">
                    <h3 className="text-lg font-bold text-slate-900">{title}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-500">{message}</p>
                </div>
                <div className="flex flex-col-reverse gap-3 px-6 py-5 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isConfirming}
                        className="rounded-xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={isConfirming}
                        className={`rounded-xl px-5 py-3 font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${confirmButtonClassName}`}
                    >
                        {isConfirming ? 'Please wait...' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

const LinkedInModal = ({
    isOpen,
    onClose,
    form,
    onChange,
    onSetConnectionMethod,
    onSubmit,
    onSubmitHostedLink,
    onSubmitCheckpoint,
    onRefreshStatus,
    isSubmitting,
    isStartingHostedLink,
    isRefreshingStatus,
    errorMessage,
    pendingCheckpoint,
    linkedinStatus,
    onRestartFlow,
}) => {
    const [showLinkedInPassword, setShowLinkedInPassword] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setShowLinkedInPassword(false);
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const checkpointType = pendingCheckpoint?.type || '';
    const needsCodeEntry = ['2FA', 'OTP', 'PHONE_REGISTER'].includes(checkpointType);
    const needsInAppValidation = checkpointType === 'IN_APP_VALIDATION' || (!checkpointType && linkedinStatus === 'PENDING');
    const hasCheckpoint = Boolean(pendingCheckpoint?.account_id);
    const checkpointHelpText = checkpointType === 'OTP'
        ? 'This code should come from LinkedIn, usually by email, SMS, or an in-app security prompt.'
        : checkpointType === '2FA'
            ? 'Use the current verification code from your authenticator app or the security method configured in LinkedIn.'
            : checkpointType === 'PHONE_REGISTER'
                ? 'LinkedIn may be asking for a phone-based verification step before the account can finish connecting.'
                : needsInAppValidation
                    ? 'Approve the LinkedIn sign-in request in the LinkedIn app or browser session, then refresh the status here.'
                    : 'Complete the LinkedIn verification step, then continue the connection here.';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
            <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.35)]">
                <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
                    <div>
                        <h3 className="text-xl font-bold text-slate-900">Connect LinkedIn Integration</h3>
                        <p className="mt-1 text-sm text-slate-500">Keep the LinkedIn login flow inside your workspace using Unipile custom authentication.</p>
                    </div>
                    <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                        <CloseIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="space-y-5 px-6 py-5">
                    {(hasCheckpoint || linkedinStatus === 'PENDING') ? (
                        <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-4">
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                <div>
                                    <h4 className="text-sm font-semibold text-slate-900">
                                        {checkpointType ? `Checkpoint: ${checkpointType}` : 'LinkedIn verification is still in progress'}
                                    </h4>
                                    <p className="mt-1 text-sm text-slate-600">
                                        {needsCodeEntry
                                            ? 'Enter the verification code from LinkedIn to continue the connection.'
                                            : needsInAppValidation
                                                ? 'Approve the LinkedIn sign-in if prompted, then refresh the status from here.'
                                                : 'Complete the remaining LinkedIn verification steps, then refresh the connection status.'}
                                    </p>
                                    <p className="mt-2 text-sm text-slate-500">
                                        {checkpointHelpText}
                                    </p>
                                    <p className="mt-2 text-xs text-slate-400">
                                        If you do not receive anything from LinkedIn, this attempt may be waiting on a different security check. You can refresh status and try a fresh credentials-based login.
                                    </p>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <button
                                        type="button"
                                        onClick={onRefreshStatus}
                                        disabled={isRefreshingStatus}
                                        className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${isRefreshingStatus ? 'cursor-not-allowed bg-slate-200 text-slate-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                    >
                                        {isRefreshingStatus ? 'Checking...' : 'Refresh Status'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onRestartFlow}
                                        className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>

                            {needsCodeEntry ? (
                                <div className="mt-4">
                                    <form onSubmit={onSubmitCheckpoint} className="flex flex-col gap-3 sm:flex-row">
                                        <input
                                            type="text"
                                            name="checkpoint_code"
                                            value={form.checkpoint_code}
                                            onChange={onChange}
                                            placeholder="Enter verification code"
                                            className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                        />
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className={`rounded-xl px-5 py-3 text-sm font-semibold transition ${isSubmitting ? 'cursor-not-allowed bg-slate-200 text-slate-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                        >
                                            {isSubmitting ? 'Submitting...' : 'Submit Code'}
                                        </button>
                                    </form>
                                    <p className="mt-3 text-xs text-slate-400">
                                        The code is sent by LinkedIn, not by Roboman. Check your LinkedIn email, phone, spam folder, and the LinkedIn mobile app.
                                    </p>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {!hasCheckpoint ? (
                        <>
                            <div className="inline-flex rounded-2xl border border-slate-200 bg-slate-50 p-1">
                                <button
                                    type="button"
                                    onClick={() => onSetConnectionMethod('credentials')}
                                    className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${form.connection_method === 'credentials' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    Credentials
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onSetConnectionMethod('link')}
                                    className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${form.connection_method === 'link' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    Link
                                </button>
                            </div>

                            {form.connection_method === 'link' ? (
                                <div className="space-y-5">
                                    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-4 text-sm text-slate-600">
                                        Use the hosted LinkedIn connection flow if you prefer the older sign-in method. This option will redirect you to the LinkedIn / Unipile authentication page and bring you back after completion.
                                    </div>

                                    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                                        <button type="button" onClick={onClose} className="rounded-xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-700 hover:bg-slate-50">
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={onSubmitHostedLink}
                                            disabled={isStartingHostedLink}
                                            className={`rounded-xl px-5 py-3 font-semibold text-white transition ${isStartingHostedLink ? 'cursor-not-allowed bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                                        >
                                            {isStartingHostedLink ? 'Redirecting...' : 'Continue With Link'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit} className="space-y-5">
                                    <div className="grid grid-cols-1 gap-5">
                                        <FormField label="LinkedIn Email or Username" required>
                                            <input type="text" name="username" value={form.username} onChange={onChange} className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
                                        </FormField>
                                    <FormField label="LinkedIn Password" required>
                                        <div className="relative">
                                            <input
                                                type={showLinkedInPassword ? 'text' : 'password'}
                                                name="password"
                                                value={form.password}
                                                onChange={onChange}
                                                autoComplete="new-password"
                                                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 pr-12 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowLinkedInPassword((previousState) => !previousState)}
                                                className="absolute inset-y-0 right-0 flex items-center px-4 text-slate-400 transition hover:text-slate-600"
                                                aria-label={showLinkedInPassword ? 'Hide password' : 'Show password'}
                                            >
                                                {showLinkedInPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                            </button>
                                        </div>
                                    </FormField>
                                </div>

                                    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-600">
                                        The LinkedIn authentication is handled in-platform. If Unipile asks for a checkpoint code or in-app approval, this modal will guide the next step.
                                    </div>

                                    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                                        <button type="button" onClick={onClose} className="rounded-xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-700 hover:bg-slate-50">
                                            Cancel
                                        </button>
                                        <button type="submit" disabled={isSubmitting} className={`rounded-xl px-5 py-3 font-semibold text-white transition ${isSubmitting ? 'cursor-not-allowed bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
                                            {isSubmitting ? 'Connecting...' : 'Connect LinkedIn'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </>
                    ) : (
                        <div className="flex justify-end">
                            <button type="button" onClick={onClose} className="rounded-xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-700 hover:bg-slate-50">
                                Close
                            </button>
                        </div>
                    )}

                    {errorMessage ? (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            {errorMessage}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

const SmtpModal = ({
    isOpen,
    onClose,
    smtpAccounts,
    onOpenAddAccount,
    onDeleteAccount,
    deletingSmtpId,
    errorMessage,
}) => {
    const hasAccounts = smtpAccounts.length > 0;

    if (!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.35)]">
                <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
                    <div>
                        <h3 className="text-xl font-bold text-slate-900">{hasAccounts ? 'Manage SMTP Integrations' : 'Connect SMTP Integration'}</h3>
                        <p className="mt-1 text-sm text-slate-500">Add and manage multiple IMAP and SMTP credentials for MAIL accounts in Unipile.</p>
                    </div>
                    <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                        <CloseIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="px-6 py-5">
                    <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <h4 className="text-sm font-semibold text-slate-900">Connected Accounts</h4>
                                <p className="mt-1 text-sm text-slate-500">
                                    {hasAccounts ? `${smtpAccounts.length} SMTP account${smtpAccounts.length > 1 ? 's are' : ' is'} available for this user.` : 'No SMTP accounts connected yet.'}
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                {hasAccounts && (
                                    <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                                        {smtpAccounts.length} Connected
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={onOpenAddAccount}
                                    className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
                                >
                                    Add New SMTP Account
                                </button>
                            </div>
                        </div>

                        {hasAccounts ? (
                            <div className="mt-4 space-y-3">
                                {smtpAccounts.map((account) => (
                                    <div key={account.smtp_integration_id} className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                                        <div className="space-y-1">
                                            <p className="text-sm font-semibold text-slate-900">{account.smtp_user || account.imap_user}</p>
                                            <p className="text-sm text-slate-500">{account.smtp_host}:{account.smtp_port}</p>
                                            <p className="text-xs text-slate-400">IMAP {account.imap_host}:{account.imap_port} | {account.imap_encryption}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => onDeleteAccount(account.smtp_integration_id)}
                                            disabled={deletingSmtpId === account.smtp_integration_id}
                                            className={`inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition ${deletingSmtpId === account.smtp_integration_id ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400' : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'}`}
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                            {deletingSmtpId === account.smtp_integration_id ? 'Removing...' : 'Remove'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>

                    {errorMessage && (
                        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            {errorMessage}
                        </div>
                    )}

                    <div className="mt-6 flex justify-end">
                        <button type="button" onClick={onClose} className="rounded-xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-700 hover:bg-slate-50">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SmtpAccountFormModal = ({
    isOpen,
    onClose,
    form,
    onChange,
    onSubmit,
    isSubmitting,
    errorMessage,
    hasAccounts,
}) => {
    if (!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm">
            <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.35)]">
                <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
                    <div>
                        <h3 className="text-xl font-bold text-slate-900">{hasAccounts ? 'Add New SMTP Account' : 'Connect SMTP Integration'}</h3>
                        <p className="mt-1 text-sm text-slate-500">Passwords are only used for the connect request and are not stored in the database.</p>
                    </div>
                    <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                        <CloseIcon className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={onSubmit} className="px-6 py-5">
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        <FormField label="IMAP User" required>
                            <input type="text" name="imap_user" value={form.imap_user} onChange={onChange} className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
                        </FormField>

                        <FormField label="SMTP User" required>
                            <input type="text" name="smtp_user" value={form.smtp_user} onChange={onChange} className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
                        </FormField>

                        <FormField label="IMAP Password" required>
                            <input type="password" name="imap_password" value={form.imap_password} onChange={onChange} autoComplete="new-password" className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
                        </FormField>

                        <FormField label="SMTP Password" required>
                            <input type="password" name="smtp_password" value={form.smtp_password} onChange={onChange} autoComplete="new-password" className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
                        </FormField>

                        <FormField label="IMAP Host" required>
                            <input type="text" name="imap_host" value={form.imap_host} onChange={onChange} placeholder="imap.yourmail.com" className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
                        </FormField>

                        <FormField label="IMAP Port" required>
                            <input type="number" name="imap_port" value={form.imap_port} onChange={onChange} placeholder="993" className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
                        </FormField>

                        <FormField label="SMTP Host" required>
                            <input type="text" name="smtp_host" value={form.smtp_host} onChange={onChange} placeholder="smtp.yourmail.com" className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
                        </FormField>

                        <FormField label="SMTP Port" required>
                            <input type="number" name="smtp_port" value={form.smtp_port} onChange={onChange} placeholder="465" className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
                        </FormField>

                        <div className="md:col-span-2">
                            <FormField label="IMAP Encryption" required>
                                <input type="text" name="imap_encryption" value="SSL" readOnly className="w-full rounded-xl border border-slate-300 bg-slate-100 px-4 py-3 text-slate-700 focus:outline-none" />
                            </FormField>
                        </div>
                    </div>

                    {errorMessage ? (
                        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            {errorMessage}
                        </div>
                    ) : null}

                    <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                        <button type="button" onClick={onClose} className="rounded-xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-700 hover:bg-slate-50">
                            Cancel
                        </button>
                        <button type="submit" disabled={isSubmitting} className={`rounded-xl px-5 py-3 font-semibold text-white transition ${isSubmitting ? 'cursor-not-allowed bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
                            {isSubmitting ? 'Connecting...' : 'Add SMTP Account'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const getSmtpFormValues = () => ({
    ...DEFAULT_SMTP_FORM,
});

const getLinkedInFormValues = () => ({
    ...DEFAULT_LINKEDIN_FORM,
});

const getErrorMessage = (data, fallbackMessage) => {
    const nestedDetail = (() => {
        if (typeof data?.detail !== 'string') {
            return null;
        }

        try {
            return JSON.parse(data.detail);
        } catch (error) {
            return null;
        }
    })();

    const errorType = data?.type || nestedDetail?.type;

    if (errorType === 'errors/unknown_authentication_context') {
        return 'LinkedIn needs an additional verification step in your browser or LinkedIn app. Complete any security prompt there, then come back and click Refresh Status again.';
    }

    if (errorType === 'errors/resource_not_found') {
        return 'The previous LinkedIn authentication session expired or no longer exists. Start a fresh connection attempt.';
    }

    const rawMessage = [
        typeof nestedDetail?.detail === 'string' ? nestedDetail.detail : '',
        typeof data?.detail === 'string' ? data.detail : '',
        typeof data?.message === 'string' ? data.message : '',
    ]
        .join(' ')
        .toLowerCase();

    if (rawMessage.includes('credentials do not match') || rawMessage.includes('correct account')) {
        return 'LinkedIn rejected this verification step because it no longer matches the current login session. Start a fresh LinkedIn connection attempt and use the most recent code.';
    }

    if (typeof nestedDetail?.detail === 'string') {
        return nestedDetail.detail;
    }

    if (typeof nestedDetail?.title === 'string') {
        return nestedDetail.title;
    }

    if (typeof data?.detail === 'string') {
        return data.detail;
    }

    if (typeof data?.message === 'string') {
        return data.message;
    }

    return fallbackMessage;
};

const isLinkedInAdditionalVerificationError = (data) => {
    if (data?.type === 'errors/unknown_authentication_context') {
        return true;
    }

    if (typeof data?.detail !== 'string') {
        return false;
    }

    try {
        const parsedDetail = JSON.parse(data.detail);
        return parsedDetail?.type === 'errors/unknown_authentication_context';
    } catch (error) {
        return false;
    }
};

const isLinkedInCheckpointCredentialMismatch = (data) => {
    const message = [
        typeof data?.detail === 'string' ? data.detail : '',
        typeof data?.message === 'string' ? data.message : '',
    ]
        .join(' ')
        .toLowerCase();

    return message.includes('credentials do not match') || message.includes('correct account');
};


/******** main component *********/
const Integrations = () => {
    const navigate = useNavigate();
    const [integrations, setIntegrations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLinkedInModalOpen, setIsLinkedInModalOpen] = useState(false);
    const [linkedInForm, setLinkedInForm] = useState(getLinkedInFormValues());
    const [linkedInErrorMessage, setLinkedInErrorMessage] = useState('');
    const [linkedInCheckpoint, setLinkedInCheckpoint] = useState(null);
    const [linkedInConnectionStatus, setLinkedInConnectionStatus] = useState('NOT_CONNECTED');
    const [isSubmittingLinkedIn, setIsSubmittingLinkedIn] = useState(false);
    const [isStartingHostedLinkedIn, setIsStartingHostedLinkedIn] = useState(false);
    const [isRefreshingLinkedInStatus, setIsRefreshingLinkedInStatus] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        confirmLabel: 'Confirm',
        tone: 'danger',
        onConfirm: null,
    });
    const [isConfirmingDialog, setIsConfirmingDialog] = useState(false);
    const [isSmtpModalOpen, setIsSmtpModalOpen] = useState(false);
    const [isSmtpAddModalOpen, setIsSmtpAddModalOpen] = useState(false);
    const [smtpForm, setSmtpForm] = useState(getSmtpFormValues());
    const [smtpAccounts, setSmtpAccounts] = useState([]);
    const [smtpErrorMessage, setSmtpErrorMessage] = useState('');
    const [isSubmittingSmtp, setIsSubmittingSmtp] = useState(false);
    const [deletingSmtpId, setDeletingSmtpId] = useState('');

    const getSmtpIntegrations = (user = {}) => {
        if (Array.isArray(user.smtp_integrations) && user.smtp_integrations.length > 0) {
            return user.smtp_integrations;
        }

        if (user.user_info?.smtp_integration) {
            return [user.user_info.smtp_integration];
        }

        return [];
    };

    const mapLinkedInIntegration = (user = {}) => {
        const linkedinConnectionStatus = user.linkedin_connection_status || 'NOT_CONNECTED';
        const linkedinPublicId = user.user_linkedin_info?.public_id;
        const linkedinUrl = user.user_info?.linkedin_address
            || (linkedinPublicId ? `linkedin.com/in/${linkedinPublicId}` : '');
        const linkedinIdentity = linkedinUrl
            || linkedinPublicId
            || user.user_info?.nick_name
            || user.email;
        const accountLabel = linkedinConnectionStatus === 'CONNECTED'
            ? (linkedinIdentity || 'LinkedIn connected')
            : linkedinConnectionStatus === 'PENDING'
                ? 'Finish LinkedIn verification in-platform.'
                : linkedinConnectionStatus === 'DISCONNECTED'
                    ? 'Reconnect LinkedIn without leaving the workspace.'
                    : 'Connect LinkedIn without leaving the workspace.';

        return {
            id: 'linkedin',
            name: 'LinkedIn Integration',
            description: 'Run LinkedIn outreach directly from the platform.',
            accountLabel,
            icon: LinkedInLogo,
            accentClass: 'bg-sky-50 text-sky-600',
            linkedin_connection_status: linkedinConnectionStatus,
            isConnected: linkedinConnectionStatus === 'CONNECTED',
            actionLabel: linkedinConnectionStatus === 'CONNECTED'
                ? 'Disconnect'
                : linkedinConnectionStatus === 'PENDING'
                    ? 'Continue Setup'
                    : linkedinConnectionStatus === 'DISCONNECTED'
                        ? 'Reconnect'
                        : 'Connect',
            showDeleteAction: linkedinConnectionStatus === 'DISCONNECTED' && Boolean(user.unipile_linkedin_id),
            deleteActionLabel: 'Delete LinkedIn',
            isActionDisabled: false,
        };
    };

    const mapSmtpIntegration = (smtpIntegrationList = []) => {
        const latestSmtpIntegration = smtpIntegrationList[0] || {};
        const isConnected = smtpIntegrationList.length > 0;
        const accountLabel = smtpIntegrationList.length > 1
            ? `${smtpIntegrationList.length} SMTP accounts connected`
            : [latestSmtpIntegration.smtp_user, latestSmtpIntegration.smtp_host]
                .filter(Boolean)
                .join(' | ') || 'Connect IMAP and SMTP credentials.';

        return {
            id: 'smtp',
            name: 'SMTP Integration',
            description: 'Add custom SMTP credentials for outbound delivery.',
            accountLabel,
            icon: SmtpLogo,
            accentClass: 'bg-blue-50 text-blue-600',
            isConnected,
            actionLabel: isConnected ? 'Manage SMTP' : 'Connect',
            isActionDisabled: false,
            smtp_integrations: smtpIntegrationList,
        };
    };

    const buildIntegrationList = (user = {}, smtpIntegrationList = []) => {
        const isEnterprise = user?.user_info?.billing_plan === 'enterprise';
        return [
            mapLinkedInIntegration(user),
            ...(isEnterprise ? BASE_STATIC_INTEGRATIONS : []),
            mapSmtpIntegration(smtpIntegrationList),
        ];
    };

    const fectchLinkedInData = async () => {
        const token = localStorage.getItem('access_token');
        setIsLoading(true);
        try {
            const response = await fetch(`${roboman_api}/users/me`, {
                method: 'GET',
                headers: {
                    Authorization: token,
                    accept: 'application/json',
                }
            });
            const data = await response.json();
            const nextSmtpAccounts = getSmtpIntegrations(data);
            setLinkedInConnectionStatus(data.linkedin_connection_status || 'NOT_CONNECTED');
            if ((data.linkedin_connection_status || 'NOT_CONNECTED') === 'CONNECTED') {
                setLinkedInCheckpoint(null);
            }
            setSmtpAccounts(nextSmtpAccounts);
            setIntegrations(buildIntegrationList(data, nextSmtpAccounts));
        }
        catch (error) {
            console.log('error occured while fetching' + error);
            setLinkedInConnectionStatus('NOT_CONNECTED');
            setSmtpAccounts([]);
            setIntegrations(buildIntegrationList());
        }
        finally {
            setIsLoading(false);
        }
    };

    // fetch
    useEffect(() => {
        fectchLinkedInData();
    }, []);

    const connectedCount = integrations.filter((integration) => integration.isConnected).length;
    const comingSoonCount = integrations.filter((integration) => integration.isActionDisabled).length;
    const needsSetupCount = integrations.filter((integration) => !integration.isConnected && !integration.isActionDisabled).length;

    const applyLinkedInResponse = async (data = {}) => {
        const nextStatus = data.linkedin_connection_status || 'NOT_CONNECTED';
        setLinkedInConnectionStatus(nextStatus);
        const nextCheckpointType = data.checkpoint?.type || data.unipile_response?.checkpoint_type || '';

        if (data.status === 'reset') {
            setLinkedInCheckpoint(null);
            setLinkedInForm(getLinkedInFormValues());
            setLinkedInErrorMessage(data.message || 'The previous LinkedIn session expired. Please try connecting again.');
            await fectchLinkedInData();
            return;
        }

        if (data.status === 'success') {
            setLinkedInCheckpoint(null);
            setLinkedInErrorMessage('');
            setLinkedInForm(getLinkedInFormValues());
            await fectchLinkedInData();
            setIsLinkedInModalOpen(false);
            return;
        }

        if (data.status === 'checkpoint_required') {
            setLinkedInCheckpoint({
                account_id: data.account_id,
                type: nextCheckpointType,
            });
            setLinkedInForm((previousState) => ({
                ...previousState,
                checkpoint_code: '',
            }));
            await fectchLinkedInData();
            return;
        }

        if (data.status === 'pending') {
            if (data.account_id && nextCheckpointType) {
                setLinkedInCheckpoint({
                    account_id: data.account_id,
                    type: nextCheckpointType,
                });
            } else {
                setLinkedInCheckpoint(null);
            }
            setLinkedInForm((previousState) => ({
                ...previousState,
                checkpoint_code: '',
            }));
            await fectchLinkedInData();
        }
    };

    const disconnectLinkedInConnection = async () => {
        const access_token = localStorage.getItem('access_token');
        try {
            const response = await fetch(`${roboman_api}/users/unipile/linkedin/disconnect`, {
                method: 'PUT',
                headers: {
                    accept: 'application/json',
                    Authorization: access_token,
                },
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                setLinkedInErrorMessage(getErrorMessage(data, 'Failed to disconnect LinkedIn integration.'));
                return;
            }

            setLinkedInCheckpoint(null);
            setLinkedInForm(getLinkedInFormValues());
            await fectchLinkedInData();
        } catch (error) {
            console.error('Error disconnecting LinkedIn integration:', error);
            setLinkedInErrorMessage('Unable to disconnect LinkedIn integration right now.');
        }
    };

    const openConfirmDialog = ({ title, message, confirmLabel, tone = 'danger', onConfirm }) => {
        setConfirmDialog(() => ({
            isOpen: true,
            title,
            message,
            confirmLabel,
            tone,
            onConfirm,
        }));
    };

    const closeConfirmDialog = () => {
        if (isConfirmingDialog) {
            return;
        }

        setConfirmDialog({
            isOpen: false,
            title: '',
            message: '',
            confirmLabel: 'Confirm',
            tone: 'danger',
            onConfirm: null,
        });
    };

    const handleConfirmDialog = async () => {
        if (typeof confirmDialog.onConfirm !== 'function') {
            closeConfirmDialog();
            return;
        }

        setIsConfirmingDialog(true);
        try {
            await confirmDialog.onConfirm();
            setConfirmDialog({
                isOpen: false,
                title: '',
                message: '',
                confirmLabel: 'Confirm',
                tone: 'danger',
                onConfirm: null,
            });
        } finally {
            setIsConfirmingDialog(false);
        }
    };

    const performDeleteLinkedInConnection = async () => {
        const access_token = localStorage.getItem('access_token');
        try {
            const response = await fetch(`${roboman_api}/users/unipile/linkedin/delete`, {
                method: 'DELETE',
                headers: {
                    accept: 'application/json',
                    Authorization: access_token,
                },
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                setLinkedInErrorMessage(getErrorMessage(data, 'Failed to delete LinkedIn integration.'));
                return;
            }

            setLinkedInCheckpoint(null);
            setLinkedInConnectionStatus('NOT_CONNECTED');
            setLinkedInForm(getLinkedInFormValues());
            await fectchLinkedInData();
        } catch (error) {
            console.error('Error deleting LinkedIn integration:', error);
            setLinkedInErrorMessage('Unable to delete LinkedIn integration right now.');
        }
    };

    const deleteLinkedInConnection = () => {
        openConfirmDialog({
            title: 'Delete LinkedIn Account',
            message: 'Delete this LinkedIn account from your integrations? This will also remove the connected account from Unipile.',
            confirmLabel: 'Delete LinkedIn',
            tone: 'danger',
            onConfirm: performDeleteLinkedInConnection,
        });
    };

    const startHostedLinkedInConnection = async () => {
        const access_token = localStorage.getItem('access_token');
        const headers = {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: access_token,
        };

        setIsStartingHostedLinkedIn(true);
        setLinkedInErrorMessage('');

        try {
            const shouldReconnect = ['DISCONNECTED', 'PENDING'].includes(linkedInConnectionStatus);
            const endpoint = shouldReconnect
                ? `${roboman_api}/users/unipile/linkedin/reconnect-auth-url`
                : `${roboman_api}/users/unipile/linkedin/auth-url`;

            const response = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    success_redirect_url: 'https://outreach.roboman.ai/dashboard',
                    failure_redirect_url: 'https://outreach.roboman.ai/dashboard',
                }),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                setLinkedInErrorMessage(getErrorMessage(data, 'Failed to start the LinkedIn hosted link flow.'));
                return;
            }

            if (data?.url) {
                window.location.href = data.url;
                return;
            }

            await fectchLinkedInData();
            setIsLinkedInModalOpen(false);
        } catch (error) {
            console.error('Error starting hosted LinkedIn auth:', error);
            setLinkedInErrorMessage('Unable to start the LinkedIn hosted link flow right now.');
        } finally {
            setIsStartingHostedLinkedIn(false);
        }
    };

    const openLinkedInModal = () => {
        setLinkedInErrorMessage('');
        setLinkedInForm(getLinkedInFormValues());
        if (linkedInConnectionStatus !== 'PENDING') {
            setLinkedInCheckpoint(null);
        }
        setIsLinkedInModalOpen(true);
    };

    const closeLinkedInModal = () => {
        setIsLinkedInModalOpen(false);
        setLinkedInErrorMessage('');
        setIsSubmittingLinkedIn(false);
        setIsStartingHostedLinkedIn(false);
        setIsRefreshingLinkedInStatus(false);
        setLinkedInForm(getLinkedInFormValues());
    };

    const handleLinkedInInputChange = (event) => {
        const { name, value } = event.target;
        setLinkedInForm((previousState) => ({
            ...previousState,
            [name]: value,
        }));
    };

    const handleLinkedInConnectionMethodChange = (connectionMethod) => {
        setLinkedInErrorMessage('');
        setLinkedInForm((previousState) => ({
            ...previousState,
            connection_method: connectionMethod,
        }));
    };

    const handleLinkedInSubmit = async (event) => {
        event.preventDefault();
        setIsSubmittingLinkedIn(true);
        setLinkedInErrorMessage('');

        const access_token = localStorage.getItem('access_token');
        const payload = {
            provider: 'LINKEDIN',
            auth_method: 'credentials',
        };

        if (!linkedInForm.username.trim() || !linkedInForm.password) {
            setLinkedInErrorMessage('LinkedIn username and password are required.');
            setIsSubmittingLinkedIn(false);
            return;
        }

        payload.username = linkedInForm.username.trim();
        payload.password = linkedInForm.password;

        try {
            const response = await fetch(`${roboman_api}/users/unipile/linkedin/native-connect`, {
                method: 'POST',
                headers: {
                    accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: access_token,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                if (isLinkedInAdditionalVerificationError(data)) {
                    setLinkedInCheckpoint(null);
                    setLinkedInConnectionStatus('PENDING');
                }
                setLinkedInErrorMessage(getErrorMessage(data, 'Failed to connect LinkedIn integration.'));
                return;
            }

            await applyLinkedInResponse(data);
        } catch (error) {
            console.error('Error during LinkedIn integration:', error);
            setLinkedInErrorMessage('Unable to connect LinkedIn integration right now.');
        } finally {
            setIsSubmittingLinkedIn(false);
        }
    };

    const handleLinkedInCheckpointSubmit = async (event) => {
        event.preventDefault();
        if (!linkedInCheckpoint?.account_id) {
            return;
        }

        if (!linkedInForm.checkpoint_code.trim()) {
            setLinkedInErrorMessage('Enter the LinkedIn verification code to continue.');
            return;
        }

        setIsSubmittingLinkedIn(true);
        setLinkedInErrorMessage('');

        try {
            const response = await fetch(`${roboman_api}/users/unipile/linkedin/checkpoint`, {
                method: 'POST',
                headers: {
                    accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: localStorage.getItem('access_token'),
                },
                body: JSON.stringify({
                    provider: 'LINKEDIN',
                    account_id: linkedInCheckpoint.account_id,
                    code: linkedInForm.checkpoint_code.trim(),
                }),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                if (isLinkedInAdditionalVerificationError(data)) {
                    setLinkedInCheckpoint(null);
                    setLinkedInConnectionStatus('PENDING');
                }
                if (isLinkedInCheckpointCredentialMismatch(data)) {
                    setLinkedInCheckpoint(null);
                    setLinkedInConnectionStatus('NOT_CONNECTED');
                    setLinkedInForm(getLinkedInFormValues());
                }
                setLinkedInErrorMessage(getErrorMessage(data, 'Failed to validate the LinkedIn checkpoint.'));
                return;
            }

            await applyLinkedInResponse(data);
            if (data.status === 'pending') {
                window.setTimeout(() => {
                    refreshLinkedInStatus();
                }, 1500);
            }
        } catch (error) {
            console.error('Error validating LinkedIn checkpoint:', error);
            setLinkedInErrorMessage('Unable to validate the LinkedIn checkpoint right now.');
        } finally {
            setIsSubmittingLinkedIn(false);
        }
    };

    const refreshLinkedInStatus = async () => {
        setIsRefreshingLinkedInStatus(true);
        setLinkedInErrorMessage('');

        try {
            const response = await fetch(`${roboman_api}/users/unipile/linkedin/native-status`, {
                method: 'GET',
                headers: {
                    accept: 'application/json',
                    Authorization: localStorage.getItem('access_token'),
                },
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                if (isLinkedInAdditionalVerificationError(data)) {
                    setLinkedInCheckpoint(null);
                    setLinkedInConnectionStatus('PENDING');
                }
                setLinkedInErrorMessage(getErrorMessage(data, 'Failed to refresh LinkedIn status.'));
                return;
            }

            await applyLinkedInResponse(data);
        } catch (error) {
            console.error('Error refreshing LinkedIn status:', error);
            setLinkedInErrorMessage('Unable to refresh LinkedIn status right now.');
        } finally {
            setIsRefreshingLinkedInStatus(false);
        }
    };

    const openSmtpModal = () => {
        setSmtpForm(getSmtpFormValues());
        setSmtpErrorMessage('');
        setIsSmtpModalOpen(true);
        setIsSmtpAddModalOpen(smtpAccounts.length === 0);
    };

    const closeSmtpModal = () => {
        setIsSmtpModalOpen(false);
        setIsSmtpAddModalOpen(false);
        setSmtpErrorMessage('');
        setIsSubmittingSmtp(false);
        setDeletingSmtpId('');
        setSmtpForm(getSmtpFormValues());
    };

    const openSmtpAddModal = () => {
        setSmtpForm(getSmtpFormValues());
        setSmtpErrorMessage('');
        setIsSmtpAddModalOpen(true);
    };

    const closeSmtpAddModal = () => {
        setIsSmtpAddModalOpen(false);
        setSmtpErrorMessage('');
        setIsSubmittingSmtp(false);
        setSmtpForm(getSmtpFormValues());
    };

    const handleIntegrationAction = (integration) => {
        if (integration.id === 'linkedin') {
            if (integration.isConnected) {
                disconnectLinkedInConnection();
                return;
            }

            openLinkedInModal();
            return;
        }

        if (integration.id === 'smtp') {
            openSmtpModal();
            return;
        }

        if (integration.id === 'domain-email') {
            navigate('/domain-email');
        }
    };

    const handleSmtpInputChange = (event) => {
        const { name, value } = event.target;
        setSmtpForm((previousState) => ({
            ...previousState,
            [name]: value,
        }));
    };

    const handleSmtpSubmit = async (event) => {
        event.preventDefault();
        setIsSubmittingSmtp(true);
        setSmtpErrorMessage('');

        const formData = new FormData(event.currentTarget);
        const submittedValues = {
            imap_user: String(formData.get('imap_user') || '').trim(),
            smtp_user: String(formData.get('smtp_user') || '').trim(),
            imap_password: String(formData.get('imap_password') || ''),
            smtp_password: String(formData.get('smtp_password') || ''),
            imap_host: String(formData.get('imap_host') || '').trim(),
            imap_port: String(formData.get('imap_port') || '').trim(),
            smtp_host: String(formData.get('smtp_host') || '').trim(),
            smtp_port: String(formData.get('smtp_port') || '').trim(),
            imap_encryption: 'SSL',
        };

        const requiredFields = [
            submittedValues.imap_user,
            submittedValues.smtp_user,
            submittedValues.imap_password,
            submittedValues.smtp_password,
            submittedValues.imap_host,
            submittedValues.imap_port,
            submittedValues.smtp_host,
            submittedValues.smtp_port,
            submittedValues.imap_encryption,
        ];

        if (requiredFields.some((value) => !String(value).trim())) {
            setSmtpErrorMessage('Please fill in all required SMTP fields.');
            setIsSubmittingSmtp(false);
            return;
        }

        const imapPort = Number(submittedValues.imap_port);
        const smtpPort = Number(submittedValues.smtp_port);

        if (!imapPort || !smtpPort) {
            setSmtpErrorMessage('IMAP port and SMTP port must be valid numbers.');
            setIsSubmittingSmtp(false);
            return;
        }

        const access_token = localStorage.getItem('access_token');
        const payload = {
            provider: 'MAIL',
            sync_limit: 'NO_HISTORY_SYNC',
            imap_user: submittedValues.imap_user,
            smtp_user: submittedValues.smtp_user,
            imap_password: submittedValues.imap_password,
            smtp_password: submittedValues.smtp_password,
            imap_host: submittedValues.imap_host,
            imap_port: imapPort,
            smtp_host: submittedValues.smtp_host,
            smtp_port: smtpPort,
            imap_encryption: submittedValues.imap_encryption,
        };

        try {
            const response = await fetch(`${roboman_api}/users/unipile/smtp/connect`, {
                method: 'POST',
                headers: {
                    accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: access_token,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                setSmtpErrorMessage(getErrorMessage(data, 'Failed to connect SMTP integration.'));
                return;
            }

            await fectchLinkedInData();
            setIsSmtpAddModalOpen(false);
            setSmtpForm(getSmtpFormValues());
            setSmtpErrorMessage('');
        } catch (error) {
            console.error('Error during SMTP integration:', error);
            setSmtpErrorMessage('Unable to connect SMTP integration right now.');
        } finally {
            setIsSubmittingSmtp(false);
        }
    };

    const performDeleteSmtp = async (smtpIntegrationId) => {
        setDeletingSmtpId(smtpIntegrationId);
        setSmtpErrorMessage('');
        const access_token = localStorage.getItem('access_token');

        try {
            const response = await fetch(`${roboman_api}/users/unipile/smtp/${smtpIntegrationId}`, {
                method: 'DELETE',
                headers: {
                    accept: 'application/json',
                    Authorization: access_token,
                },
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                setSmtpErrorMessage(getErrorMessage(data, 'Failed to remove SMTP integration.'));
                return;
            }

            await fectchLinkedInData();
        } catch (error) {
            console.error('Error removing SMTP integration:', error);
            setSmtpErrorMessage('Unable to remove SMTP integration right now.');
        } finally {
            setDeletingSmtpId('');
        }
    };

    const handleDeleteSmtp = (smtpIntegrationId) => {
        openConfirmDialog({
            title: 'Remove SMTP Account',
            message: 'Remove this SMTP account from your integrations? This will also delete the related account from Unipile.',
            confirmLabel: 'Remove SMTP',
            tone: 'danger',
            onConfirm: () => performDeleteSmtp(smtpIntegrationId),
        });
    };

    return (
        <div className="p-6 sm:p-8 lg:p-10">
            <SectionCard
                title="Integrations"
                subtitle="Connect your favorite tools to streamline your workflow and centralize your data."
            >
                <Card className="space-y-5">
                    <div className="rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)] sm:p-5">
                        <div className="flex flex-wrap gap-3">
                            <SummaryChip label="Connected" value={connectedCount} tone="green" />
                            <SummaryChip label="Needs Setup" value={needsSetupCount} tone="blue" />
                            <SummaryChip label="Coming Soon" value={comingSoonCount} tone="amber" />
                        </div>
                    </div>

                    {isLoading ? (
                        <LoadingCards />
                    ) : (
                        integrations.length === 0 ? (
                            <div className="rounded-[24px] border border-dashed border-slate-300 bg-white/70 px-6 py-14 text-center">
                                <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
                                    <SearchIcon className="h-6 w-6 text-slate-400" />
                                </div>
                                <p className="mt-4 text-base font-semibold text-slate-700">No integrations available</p>
                                <p className="mt-2 text-sm text-slate-400">Integration options will appear here when they are ready.</p>
                            </div>
                        ) : (
                            <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 ${integrations.length === 3 ? 'xl:grid-cols-3' : integrations.length === 2 ? 'xl:grid-cols-2' : 'xl:grid-cols-4'}`}>
                                {integrations.map((integration) => (
                                    <div
                                        key={integration.id}
                                        className="group flex min-h-[208px] flex-col rounded-2xl border border-slate-200 bg-white/92 p-4 shadow-[0_10px_30px_rgba(15,23,42,0.05)] transition duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_18px_45px_rgba(15,23,42,0.08)]"
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${integration.accentClass}`}>
                                                <integration.icon className="h-5 w-5" />
                                            </div>
                                            <span
                                                className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${integration.isConnected
                                                    ? 'bg-emerald-50 text-emerald-700'
                                                    : integration.isActionDisabled
                                                        ? 'bg-amber-50 text-amber-700'
                                                        : 'bg-slate-100 text-slate-500'
                                                    }`}
                                            >
                                                {integration.isConnected ? 'Connected' : integration.isActionDisabled ? 'Planned' : 'Not Connected'}
                                            </span>
                                        </div>

                                        <div className="mt-4 space-y-2">
                                            <h4 className="text-base font-semibold tracking-tight text-slate-900">{integration.name}</h4>
                                            {integration.accountLabel ? (
                                                <p title={integration.accountLabel} className="truncate text-xs font-medium text-slate-400">
                                                    {integration.accountLabel}
                                                </p>
                                            ) : null}
                                            <p className="text-sm leading-6 text-slate-500">
                                                {integration.description}
                                            </p>
                                        </div>

                                        <div className="mt-auto border-t border-slate-100 pt-4">
                                            <div className="flex flex-col gap-2">
                                                <button
                                                    onClick={() => !integration.isActionDisabled && handleIntegrationAction(integration)}
                                                    disabled={integration.isActionDisabled}
                                                    className={`inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition ${integration.isActionDisabled
                                                        ? 'cursor-not-allowed bg-slate-100 text-slate-400'
                                                        : integration.id === 'linkedin' && integration.isConnected
                                                            ? 'border border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                                                            : 'bg-slate-900 text-white hover:bg-slate-800'
                                                        }`}
                                                >
                                                    {integration.actionLabel}
                                                </button>
                                                {integration.id === 'linkedin' && integration.showDeleteAction ? (
                                                    <button
                                                        type="button"
                                                        onClick={deleteLinkedInConnection}
                                                        className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700"
                                                    >
                                                        {integration.deleteActionLabel}
                                                    </button>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                </Card>

                <LinkedInModal
                    isOpen={isLinkedInModalOpen}
                    onClose={closeLinkedInModal}
                    form={linkedInForm}
                    onChange={handleLinkedInInputChange}
                    onSetConnectionMethod={handleLinkedInConnectionMethodChange}
                    onSubmit={handleLinkedInSubmit}
                    onSubmitHostedLink={startHostedLinkedInConnection}
                    onSubmitCheckpoint={handleLinkedInCheckpointSubmit}
                    onRefreshStatus={refreshLinkedInStatus}
                    isSubmitting={isSubmittingLinkedIn}
                    isStartingHostedLink={isStartingHostedLinkedIn}
                    isRefreshingStatus={isRefreshingLinkedInStatus}
                    errorMessage={linkedInErrorMessage}
                    pendingCheckpoint={linkedInCheckpoint}
                    linkedinStatus={linkedInConnectionStatus}
                    onRestartFlow={() => {
                        setLinkedInCheckpoint(null);
                        setLinkedInErrorMessage('');
                        setLinkedInConnectionStatus('NOT_CONNECTED');
                        setLinkedInForm(getLinkedInFormValues());
                    }}
                />

                <ConfirmModal
                    isOpen={confirmDialog.isOpen}
                    title={confirmDialog.title}
                    message={confirmDialog.message}
                    confirmLabel={confirmDialog.confirmLabel}
                    onCancel={closeConfirmDialog}
                    onConfirm={handleConfirmDialog}
                    isConfirming={isConfirmingDialog}
                    tone={confirmDialog.tone}
                />

                <SmtpModal
                    isOpen={isSmtpModalOpen}
                    onClose={closeSmtpModal}
                    smtpAccounts={smtpAccounts}
                    onOpenAddAccount={openSmtpAddModal}
                    onDeleteAccount={handleDeleteSmtp}
                    deletingSmtpId={deletingSmtpId}
                    errorMessage={isSmtpAddModalOpen ? '' : smtpErrorMessage}
                />

                <SmtpAccountFormModal
                    isOpen={isSmtpAddModalOpen}
                    onClose={closeSmtpAddModal}
                    form={smtpForm}
                    onChange={handleSmtpInputChange}
                    onSubmit={handleSmtpSubmit}
                    isSubmitting={isSubmittingSmtp}
                    errorMessage={smtpErrorMessage}
                    hasAccounts={smtpAccounts.length > 0}
                />
            </SectionCard>
        </div>
    );
};

export default Integrations;
